import type { DialogProps } from "@mui/material";
import classNames from "classnames";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppInput } from "../../../../shared/components/AppInput";
import { AppLabel } from "../../../../shared/components/AppLabel";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { ModalBasic } from "../../../../shared/components/ModalBasic";
import { TModalsIds } from "../../../../app/state/modalSlice/types";
import { TValueForService } from "../../../../services/api/instances/storageApi/types/service_types";
import { getSpaUpdatedValue } from "../../helpers/getSpaUpdatedValue";
import { selectSpaValue } from "../../redux/selectors";
import { isLatinAndStandardChars } from "../../../../shared/utils/isLatinAndStandardChars";
import { createNotification } from "../../../../app/state/notificationSlice/slice";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { useModalSelectors } from "../../../../app/state/modalSlice/hook/use-modal-selectors";
import styles from "./styles.module.css";

interface Props extends Omit<DialogProps, "open"> {
  saveSpa: (value: TValueForService<"ai_agents_spa">) => void;
  closeModal: (id: TModalsIds) => void;
  uri: string;
  id: string;
}

export const SpaNameChangeModal = (props: Props) => {
  const dispatch = useAppDispatch();
  const spaValue = useAppSelector(selectSpaValue);
  const spaNameChangeModal = useModalSelectors("spa_name_change_modal");
  const [spaName, setSpaName] = useState<string>(props.uri);
  const [isError, setIsError] = useState<boolean>(false);
  const hasNotifiedRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    setIsError(false);
  }, [spaNameChangeModal.isOpen]);

  useEffect(() => {
    if (!props.uri) {
      setSpaName("");
      return;
    }
    setSpaName(props.uri);
  }, [props.uri]);

  const handleSpaNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value.toLowerCase();
    if (!isLatinAndStandardChars(newValue)) {
      setIsError(true);
      if (!hasNotifiedRef.current) {
        hasNotifiedRef.current = true;
        dispatch(
          createNotification("warning", t("skills.input_warning"), 3000),
        );
      }

      return;
    }

    if (isError) setIsError(false);
    if (hasNotifiedRef.current) hasNotifiedRef.current = false;

    setSpaName(newValue);
  };

  const updateSpaObject = useCallback(() => {
    if (!spaValue) return;
    const updatedValue = getSpaUpdatedValue(spaValue, {
      uri_object: {
        ...spaValue?.spa_object?.uri_object,
        uri: {
          relative_uri: `spa-${spaName}-${props.id}.gureev.pro`,
          module: spaName,
          id: props.id,
        },
      },
    });
    props.saveSpa(updatedValue);
  }, [props.id, spaName]);

  return (
    <ModalBasic
      open={spaNameChangeModal.isOpen}
      closeModal={() => props.closeModal("spa_name_change_modal")}
      onClose={() => props.closeModal("spa_name_change_modal")}
      onOk={() => {
        updateSpaObject();
        props.closeModal("spa_name_change_modal");
      }}
      onCancel={() => {
        setSpaName(props.uri || "");
        props.closeModal("spa_name_change_modal");
      }}
      title={"Изменить имя"}
      buttonOkText={"Подтвердить"}
      hasHeader
      hasFooter
    >
      <div className={styles.body}>
        <AppLabel>{`https://spa-${spaName}-${props.id}.gureev.pro`}</AppLabel>
        <AppInput
          value={spaName || ""}
          onChange={handleSpaNameChange}
          placeholder={t("editor.change_name_validation_placeholder")}
          className={classNames(isError && styles["input-error"])}
        />
        <span
          className={classNames(styles.error, isError && styles["with-error"])}
        >
          {t("editor.change_name_validation_error")}
        </span>
      </div>
    </ModalBasic>
  );
};
